import axios from 'axios'

// 请求缓存服务
class RequestCache {
  private cache: Map<string, { data: any; timestamp: number; expireTime: number }> = new Map()
  private defaultExpireTime = 5 * 60 * 1000 // 默认5分钟

  set(key: string, data: any, expireTime: number = this.defaultExpireTime) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expireTime
    })
  }

  get(key: string): any | null {
    const item = this.cache.get(key)
    if (!item) return null

    // 检查是否过期
    if (Date.now() - item.timestamp > item.expireTime) {
      this.cache.delete(key)
      return null
    }

    return item.data
  }

  has(key: string): boolean {
    return this.get(key) !== null
  }

  delete(key: string) {
    this.cache.delete(key)
  }

  clear() {
    this.cache.clear()
  }

  // 生成缓存key
  static generateKey(url: string, params?: any): string {
    return params ? `${url}?${JSON.stringify(params)}` : url
  }
}

// 全局Loading状态管理
class LoadingManager {
  private loaders: Map<string, number> = new Map()

  start(key: string) {
    const count = this.loaders.get(key) || 0
    this.loaders.set(key, count + 1)
    this.updateGlobalLoader()
  }

  end(key: string) {
    const count = this.loaders.get(key) || 0
    if (count > 0) {
      this.loaders.set(key, count - 1)
    }
    this.updateGlobalLoader()
  }

  private updateGlobalLoader() {
    const totalLoading = Array.from(this.loaders.values()).reduce((a, b) => a + b, 0)
    // 可以通过provide/inject或store来控制全局loading状态
    window.dispatchEvent(new CustomEvent('api-loading', { 
      detail: { loading: totalLoading > 0, count: totalLoading }
    }))
  }
}

// 请求重试服务
class RetryManager {
  async execute(fn: () => Promise<any>, maxRetries: number = 3, delay: number = 1000): Promise<any> {
    let lastError: any
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn()
      } catch (error: any) {
        lastError = error
        
        // 如果是网络错误或服务器错误，可以重试
        if (this.shouldRetry(error) && i < maxRetries - 1) {
          await this.sleep(delay * Math.pow(2, i)) // 指数退避
          continue
        }
        
        break
      }
    }
    
    throw lastError
  }

  private shouldRetry(error: any): boolean {
    // 网络错误或服务器错误可以重试
    if (!error.response) return true
    if (error.response.status >= 500) return true
    if (error.response.status === 429) return true // 限流
    return false
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

// 安全存储服务
class SecureStorage {
  // 存储令牌
  setToken(token: string, refreshToken: string) {
    try {
      // 使用localStorage存储，但添加加密
      const encryptedToken = this.encrypt(token)
      const encryptedRefreshToken = this.encrypt(refreshToken)
      localStorage.setItem('secure_token', encryptedToken)
      localStorage.setItem('secure_refresh_token', encryptedRefreshToken)
      localStorage.setItem('token_expiry', (Date.now() + 30 * 24 * 3600000).toString()) // 30天，与后端JWT有效期(expiresIn:30d)对齐，避免1小时后被路由守卫误踢到登录页
      // 会话级登录标记：仅当前浏览器会话有效，关闭浏览器后自动失效，重新打开域名需先登录
      try { sessionStorage.setItem('admin_session', '1') } catch (_) { /* 忽略 */ }
    } catch (error) {
      console.error('存储令牌失败:', error)
    }
  }

  // 获取令牌
  getToken(): string | null {
    try {
      const encryptedToken = localStorage.getItem('secure_token')
      const expiry = localStorage.getItem('token_expiry')
      
      if (!encryptedToken || !expiry) {
        return null
      }
      
      // 检查令牌是否过期
      if (Date.now() > parseInt(expiry)) {
        this.refreshToken()
        return null
      }
      
      return this.decrypt(encryptedToken)
    } catch (error) {
      console.error('获取令牌失败:', error)
      return null
    }
  }

  // 获取刷新令牌
  getRefreshToken(): string | null {
    try {
      const encryptedRefreshToken = localStorage.getItem('secure_refresh_token')
      return encryptedRefreshToken ? this.decrypt(encryptedRefreshToken) : null
    } catch (error) {
      console.error('获取刷新令牌失败:', error)
      return null
    }
  }

  // 刷新令牌
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        this.clearTokens()
        window.location.href = '/login'
        return
      }

      const response = await axios.post('/api/auth/refresh', { refreshToken })
      if (response.data && response.data.token && response.data.refreshToken) {
        this.setToken(response.data.token, response.data.refreshToken)
      } else {
        this.clearTokens()
        window.location.href = '/login'
      }
    } catch (error) {
      console.error('刷新令牌失败:', error)
      this.clearTokens()
      window.location.href = '/login'
    }
  }

  // 清除令牌
  clearTokens() {
    localStorage.removeItem('secure_token')
    localStorage.removeItem('secure_refresh_token')
    localStorage.removeItem('token_expiry')
    try { sessionStorage.removeItem('admin_session') } catch (_) { /* 忽略 */ }
  }

  // 简单的加密/解密方法（实际项目中应使用更安全的加密方式）
  private encrypt(text: string): string {
    return btoa(unescape(encodeURIComponent(text)))
  }

  private decrypt(encrypted: string): string {
    return decodeURIComponent(escape(atob(encrypted)))
  }
}

// 请求频率限制服务
class RateLimiter {
  private requests: Map<string, { count: number; lastReset: number }> = new Map()
  private windowMs = 60000 // 1分钟
  private maxRequests = 60 // 每分钟最多60个请求

  // 检查是否超过频率限制
  checkLimit(key: string): boolean {
    const now = Date.now()
    const windowStart = now - this.windowMs

    if (!this.requests.has(key)) {
      this.requests.set(key, { count: 1, lastReset: now })
      return true
    }

    const request = this.requests.get(key)!
    
    // 重置窗口
    if (request.lastReset < windowStart) {
      request.count = 1
      request.lastReset = now
      return true
    }

    // 检查是否超过限制
    if (request.count >= this.maxRequests) {
      return false
    }

    // 增加计数
    request.count++
    return true
  }
}

const isDevLocalhost = (() => {
  try {
    if (typeof window === 'undefined') return true
    const host = window.location.hostname || ''
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '' || host.endsWith('.local')
    return import.meta.env.DEV || isLocal
  } catch (_e) {
    return true
  }
})()

const resolveBaseUrl = () => {
  // v1.3.84修复：独立域名部署，所有API路径已自带/api前缀
  // 后台管理系统（谐伴智能）与谐伴服务使用独立域名时，通过Nginx反向代理/api到后端
  // 开发环境通过vite.config.cjs中的proxy代理到localhost:3000
  const envBase = import.meta.env.VITE_API_BASE_URL
  if (envBase) return envBase
  return ''
}

// API服务封装
class ApiService {
  private apiClient = axios.create({
    baseURL: resolveBaseUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  })
  private secureStorage = new SecureStorage()
  private rateLimiter = new RateLimiter()
  private cache = new RequestCache()
  private loadingManager = new LoadingManager()
  private retryManager = new RetryManager()

  constructor() {
    // 请求拦截器
    this.apiClient.interceptors.request.use(
      async config => {
        // 检查频率限制
        const requestKey = `${config.method}:${config.url}`
        if (!this.rateLimiter.checkLimit(requestKey)) {
          return Promise.reject(new Error('请求频率过高，请稍后再试'))
        }

        // 添加认证token
        const token = this.secureStorage.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // 添加请求签名
        config.headers['X-Request-Signature'] = this.generateRequestSignature(config)

        // 添加时间戳
        config.headers['X-Request-Timestamp'] = Date.now().toString()

        // 添加随机数
        config.headers['X-Request-Nonce'] = Math.random().toString(36).substring(2, 15)

        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.apiClient.interceptors.response.use(
      response => {
        return response.data
      },
      async error => {
        // V1.3.92：统一错误信息中文化——把 axios 默认英文 "Request failed with status code XXX"
        // 替换为中文提示，优先取后端返回的 message，再按状态码兜底，避免后台UI出现英文报错
        const status = error.response ? error.response.status : 0
        const backendMsg = error.response && error.response.data && error.response.data.message
          ? String(error.response.data.message) : ''
        let zhMsg = ''
        // 统一错误处理
        if (error.response) {
          // 服务器返回错误
          switch (status) {
            case 401:
              zhMsg = backendMsg || '登录已过期，请重新登录'
              // 未认证，尝试刷新令牌
              const originalRequest = error.config
              if (!originalRequest._retry) {
                originalRequest._retry = true
                try {
                  await this.secureStorage.refreshToken()
                  const newToken = this.secureStorage.getToken()
                  if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`
                    return this.apiClient(originalRequest)
                  }
                } catch (refreshError) {
                  // 刷新令牌失败，跳转到登录页
                  this.secureStorage.clearTokens()
                  window.location.href = '/login'
                }
              }
              break
            case 403:
              zhMsg = backendMsg || '无权限访问该资源'
              break
            case 404:
              zhMsg = backendMsg || '请求的资源不存在（404）'
              break
            case 429:
              zhMsg = backendMsg || '请求频率过高，请稍后再试'
              break
            case 500:
              zhMsg = backendMsg || '服务器内部错误，请稍后重试'
              break
            default:
              zhMsg = backendMsg || `请求失败（状态码 ${status}）`
          }
        } else if (error.request) {
          // 请求已发出，但没有收到响应
          zhMsg = '网络错误，无法连接到服务器，请检查网络'
        } else {
          // 请求配置出错
          zhMsg = '请求配置错误：' + (error.message || '')
        }
        // 用中文消息覆盖 axios 默认英文消息，供前端 catch 后 ElMessage.error 展示
        try { error.message = zhMsg } catch (_) { /* 只读属性时忽略 */ }
        return Promise.reject(error)
      }
    )
  }

  // 生成请求签名（兼容中文/Unicode）
  // 注意：只对请求体计算【定长哈希摘要】拼进签名，绝不能把整个 body base64 塞进请求头，
  // 否则批量导入（如30道题）时 X-Request-Signature 会超过服务器/网关请求头上限，返回 431。
  private generateRequestSignature(config: any): string {
    const timestamp = Date.now().toString()
    const nonce = Math.random().toString(36).substring(2, 15)
    const method = config.method?.toUpperCase() || 'GET'
    const url = config.url || ''
    const data = config.data ? JSON.stringify(config.data) : ''
    // FNV-1a 32位哈希，输出固定 8 位十六进制摘要（与 body 大小无关）
    const dataDigest = this.fnv1aHex(data)
    const raw = `${timestamp}:${nonce}:${method}:${url}:${dataDigest}`
    try {
      return btoa(unescape(encodeURIComponent(raw)))
    } catch (_e) {
      const safe = `${timestamp}:${nonce}:${method}:${url}`
      try { return btoa(safe) } catch { return safe.slice(0, 32) }
    }
  }

  // FNV-1a 32位哈希：对字符串 UTF-8 字节计算，返回定长 8 位十六进制（小写）
  private fnv1aHex(str: string): string {
    if (!str) return '00000000'
    let hash = 0x811c9dc5
    const utf8 = unescape(encodeURIComponent(str))
    for (let i = 0; i < utf8.length; i++) {
      hash ^= utf8.charCodeAt(i)
      // 32位 FNV 质数乘法（用 Math.imul 保证整数溢出行为一致）
      hash = Math.imul(hash, 0x01000193)
    }
    // 转无符号 32 位再补零到 8 位 hex
    return (hash >>> 0).toString(16).padStart(8, '0')
  }

  // 通用GET请求 - 支持缓存和重试
  async get(url: string, params?: any, options?: { 
    cache?: boolean; 
    cacheTime?: number; 
    retry?: boolean;
    loading?: boolean 
  }) {
    const { cache = false, cacheTime, retry = true, loading = false } = options || {}
    const cacheKey = RequestCache.generateKey(url, params)
    const lsKey = 'xieban_client_api_get_' + cacheKey.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 160)

    // 内存缓存优先
    if (cache) {
      const cachedData = this.cache.get(cacheKey)
      if (cachedData) {
        return cachedData
      }
    }

    if (loading) {
      this.loadingManager.start(cacheKey)
    }

    const request = async () => {
      const response = await this.apiClient.get(url, { params })
      
      // 成功写入双缓存：内存 + localStorage
      if (cache && response) {
        this.cache.set(cacheKey, response, cacheTime)
      }
      try { localStorage.setItem(lsKey, JSON.stringify({ d: response, t: Date.now() })) } catch (_) {}
      
      return response
    }

    try {
      if (retry) {
        return await this.retryManager.execute(request)
      } else {
        return await request()
      }
    } catch (error) {
      console.error(`GET请求失败: ${url}，尝试localStorage兜底`, error)
      try {
        const raw = localStorage.getItem(lsKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          return parsed && (parsed.d !== undefined) ? parsed.d : parsed
        }
      } catch (_) { /* 兜底失败则抛原始错误 */ }
      throw error
    } finally {
      if (loading) {
        this.loadingManager.end(cacheKey)
      }
    }
  }

  // 通用POST请求 - 支持重试 + localStorage fallback
  async post(url: string, data?: any, options?: { retry?: boolean; loading?: boolean; persistToLocal?: boolean }) {
    const { retry = true, loading = false, persistToLocal = true } = options || {}
    const cacheKey = url
    const rawKey = (url || '') + (data ? '::' + JSON.stringify(data) : '')
    let lsKey = 'xieban_client_api_post_'
    try { lsKey += btoa(rawKey).slice(0, 160) } catch (_) { lsKey += String(rawKey).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 160) }

    if (loading) {
      this.loadingManager.start(cacheKey)
    }

    try {
      const run = async () => {
        const res = await this.apiClient.post(url, data)
        if (persistToLocal) {
          try { localStorage.setItem(lsKey, JSON.stringify({ d: res, t: Date.now() })) } catch (_) {}
        }
        return res
      }
      if (retry) {
        return await this.retryManager.execute(run)
      } else {
        return await run()
      }
    } catch (error) {
      console.error(`POST请求失败: ${url}，尝试localStorage兜底`, error)
      try {
        const raw = localStorage.getItem(lsKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          return parsed && (parsed.d !== undefined) ? parsed.d : parsed
        }
      } catch (_) { /* 兜底失败抛原错误 */ }
      throw error
    } finally {
      if (loading) {
        this.loadingManager.end(cacheKey)
      }
    }
  }

  // 通用PUT请求
  async put(url: string, data?: any) {
    try {
      return await this.apiClient.put(url, data)
    } catch (error) {
      console.error(`PUT请求失败: ${url}`, error)
      throw error
    }
  }

  // 通用DELETE请求
  async delete(url: string, params?: any) {
    try {
      return await this.apiClient.delete(url, { params })
    } catch (error) {
      console.error(`DELETE请求失败: ${url}`, error)
      throw error
    }
  }

  // 通用PATCH请求
  async patch(url: string, data?: any) {
    try {
      return await this.apiClient.patch(url, data)
    } catch (error) {
      console.error(`PATCH请求失败: ${url}`, error)
      throw error
    }
  }

  // 上传文件
  async upload(url: string, file: File, data?: any) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      if (data) {
        Object.keys(data).forEach(key => {
          formData.append(key, data[key])
        })
      }

      return await this.apiClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    } catch (error) {
      console.error(`上传文件失败: ${url}`, error)
      throw error
    }
  }

  // 下载文件
  async download(url: string, params?: any) {
    try {
      return await this.apiClient.get(url, {
        params,
        responseType: 'blob'
      })
    } catch (error) {
      console.error(`下载文件失败: ${url}`, error)
      throw error
    }
  }

  // 安全相关方法
  // 存储令牌
  setToken(token: string, refreshToken: string) {
    this.secureStorage.setToken(token, refreshToken)
  }

  // 清除令牌
  clearTokens() {
    this.secureStorage.clearTokens()
  }

  // 检查是否已认证
  isAuthenticated(): boolean {
    return this.secureStorage.getToken() !== null
  }

  // 检查权限
  async checkPermission(permission: string): Promise<boolean> {
    try {
      const response = await this.apiClient.post('/auth/check-permission', {
        permission
      })
      return response.data
    } catch (error) {
      console.error('检查权限失败:', error)
      return false
    }
  }

  // 验证令牌
  async validateToken(): Promise<boolean> {
    try {
      const response = await this.apiClient.post('/api/auth/validate-token')
      return response.data.valid
    } catch (error) {
      console.error('验证令牌失败:', error)
      return false
    }
  }
}

// 权限检查装饰器
export function requirePermission(permission: string) {
  return function(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value
    descriptor.value = async function(...args: any[]) {
      const apiService = new ApiService()
      const hasPermission = await apiService.checkPermission(permission)
      if (!hasPermission) {
        throw new Error(`缺少权限: ${permission}`)
      }
      return originalMethod.apply(this, args)
    }
  }
}

// 导出API服务实例
export const apiService = new ApiService()

/**
 * V1.3.29：带管理员 JWT 的 fetch 封装。
 * 历史页面（如 AI工具模块的 8 个父页面 + 多个孙页面）使用原生 fetch 调用
 * /api/ai/* 等受 authMiddleware 保护的端点，未携带 Authorization 头导致 401、卡片恒为 0。
 * 本函数与 axios 拦截器同口径从 localStorage.secure_token 读取并注入 Bearer token。
 *
 * V1.3.78 修复：secure_token 存的是 btoa 密文，此前多个页面直接把密文拼进 Bearer，
 * 后端 jwt.verify 必然返回"令牌无效或已过期"。统一在此解密，并兼容：
 *   1) 明文 JWT 形态的 secure_token（三段式 a.b.c，历史数据/他端写入）
 *   2) 明文兜底 key xieban_token
 *   3) "null"/"undefined"/空串等非法值一律不发 Authorization
 */
/**
 * 同步读取当前管理员 JWT 明文（供需要手工构造 headers 的场景使用）。
 * 口径与 authFetch 完全一致：btoa 密文解密 / 明文 JWT 直通 / xieban_token 兜底 / 非法值返回 null。
 */
export const getAdminToken = (): string | null => {
  try {
    let token: string | null = null
    const enc = localStorage.getItem('secure_token')
    const expiry = localStorage.getItem('token_expiry')
    const INVALID = new Set(['', 'null', 'undefined'])
    if (enc && !INVALID.has(enc) && (!expiry || Date.now() < parseInt(expiry))) {
      if (enc.split('.').length === 3) {
        token = enc
      } else {
        try {
          const decoded = decodeURIComponent(escape(atob(enc)))
          token = INVALID.has(decoded) ? null : decoded
        } catch (_) { token = null }
      }
    }
    if (!token) {
      const plain = localStorage.getItem('xieban_token')
      if (plain && !INVALID.has(plain)) token = plain
    }
    return token
  } catch (_) { return null }
}

export const authFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  let headers = new Headers(init?.headers || {})
  try {
    if (!headers.has('Authorization')) {
      const token = getAdminToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
    }
  } catch (_) { /* 读取失败则走无 token 请求（与原裸 fetch 行为一致） */ }
  return fetch(input, { ...init, headers })
}

// 导出各个服务的API调用方法
export const authApi = {
  login: (data: any) => apiService.post('/api/auth/login', data),
  logout: () => apiService.post('/api/auth/logout'),
  register: (data: any) => apiService.post('/api/auth/register'),
  refreshToken: () => apiService.post('/api/auth/refresh')
}

export const userApi = {
  // 修复：对接后端真实API，使用admin-stats路由获取用户统计数据
  getUsers: (params?: any) => apiService.get('/api/admin/stats/overview', params),
  // ★V1.3.30修复：用户列表对接后端真实用户表接口/api/data/users（原误指向stats统计接口导致列表永远为空）
  getUserList: (params?: any) => apiService.get('/api/data/users', params),
  getUser: (id: string) => apiService.get(`/api/data/users/${id}`),
  // V1.3.4：原 /api/admin/users 端点不存在（无此挂载，必 404），统一改走真实 CRUD /api/data/users
  createUser: (data: any) => apiService.post('/api/data/users', data),
  updateUser: (id: string, data: any) => apiService.put(`/api/data/users/${id}`, data),
  deleteUser: (id: string) => apiService.delete(`/api/data/users/${id}`),
  // V1.0.15：用户流量控制（推流/限流/屏蔽/付费推流）——真实生效于客户端服务列表与AI推荐
  getTrafficControls: (params?: any) => apiService.get('/api/data/traffic-controls', params),
  getTrafficSummary: () => apiService.get('/api/data/traffic-controls/summary'),
  saveTrafficControl: (data: any) => apiService.post('/api/data/traffic-controls', data),
  removeTrafficControl: (userId: string) => apiService.delete(`/api/data/traffic-controls/${userId}`)
}

export const orderApi = {
  getOrders: (params?: any) => apiService.get('/api/data/orders', params),
  getOrder: (id: string) => apiService.get(`/api/data/orders/${id}`),
  createOrder: (data: any) => apiService.post('/api/data/orders', data),
  updateOrder: (id: string, data: any) => apiService.put(`/api/data/orders/${id}`, data),
  deleteOrder: (id: string) => apiService.delete(`/api/data/orders/${id}`),
  // V1.3.57：管理员取消订单（真实写库）——后端PUT /api/data/orders/:id/cancel，管理员可取消任意状态订单，需传取消原因
  cancelOrder: (id: string, reason: string) => apiService.put(`/api/data/orders/${id}/cancel`, { reason }),
  // V1.5.8：投诉订单资金冻结——暂停/恢复订单结算与微信分账（仅管理员）
  setSettlementHold: (id: string, hold: boolean, reason?: string) =>
    apiService.post(`/api/data/orders/${id}/settlement-hold`, { hold, reason }),
  // 修复：补充退款列表API（真实Service调用，原硬编码假数据移除）
  getRefunds: (params?: any) => apiService.get('/api/data/orders/refunds', params),
  // 修复：补充订单AI分析API（真实Service调用，原硬编码假数据移除）
  getAiAnalysis: (params?: any) => apiService.get('/api/orders/ai-analysis', params),
  // 修复：补充订单统计API（真实Service调用，原硬编码假数据移除）
  getStats: (params?: any) => apiService.get('/api/data/orders/stats', params)
}

// 修复：新增财务相关API（真实Service调用，原硬编码假数据移除）
export const financeApi = {
  // 获取财务概览数据（顶部统计卡片+交易列表）→ 对齐后端真实接口（字段契约一致）
  getOverview: (params?: any) => apiService.get('/api/admin/stats/finance', params),
  // 获取交易流水列表
  getTransactions: (params?: any) => apiService.get('/api/finance/transactions', params),
  // 获取交易详情
  getTransaction: (id: string) => apiService.get(`/api/finance/transactions/${id}`),
  // 新增交易
  createTransaction: (data: any) => apiService.post('/api/finance/transactions', data),
  // 更新交易
  updateTransaction: (id: string, data: any) => apiService.put(`/api/finance/transactions/${id}`, data),
  // 删除交易
  deleteTransaction: (id: string) => apiService.delete(`/api/finance/transactions/${id}`),
  // 获取财务指标分析数据
  getFinancialMetrics: (params?: any) => apiService.get('/api/finance/metrics', params),
  // 获取预算管理数据
  getBudgets: (params?: any) => apiService.get('/api/finance/budgets', params),
  // 获取报表数据
  getReportData: (params?: any) => apiService.get('/api/finance/report', params),
  // 获取财务详情数据
  getDetailData: (params?: any) => apiService.get('/api/finance/details', params),
  // 获取资金流水数据
  getTransactionsFlow: (params?: any) => apiService.get('/api/finance/flow', params),
  // 获取月度营收图表数据
  getMonthlyRevenue: (params?: any) => apiService.get('/api/finance/monthly-revenue', params),
  // 获取收支分布图表数据
  getDistribution: (params?: any) => apiService.get('/api/finance/distribution', params),
  // 获取结算图表数据
  getSettlementChart: (params?: any) => apiService.get('/api/finance/settlement-chart', params),
  // 获取财务AI分析数据
  getAiAnalysis: (params?: any) => apiService.get('/api/finance/ai-analysis', params)
}

// V1.3.34 通知相关API封装（铃铛红点/通知中心/仪表盘通知统一使用 5 条 REST）
export const notificationApi = {
  // GET /api/admin/notifications/list 分页+过滤
  list: (params?: any) => apiService.get('/api/admin/notifications/list', params),
  // GET /api/admin/notifications/unread-count 未读数
  unreadCount: () => apiService.get('/api/admin/notifications/unread-count'),
  // PUT /api/admin/notifications/mark-read/:id → body: {status:'read'|'unread'|'processed'}
  markRead: (id: string, payload?: any) => apiService.put(`/api/admin/notifications/mark-read/${id}`, payload || { status: 'read' }),
  // PUT /api/admin/notifications/mark-all-read
  markAllRead: () => apiService.put('/api/admin/notifications/mark-all-read'),
  // POST /api/admin/notifications/create 人工发系统通知
  create: (payload: any) => apiService.post('/api/admin/notifications/create', payload || {}),
  // DELETE /api/admin/notifications/:id 管理员永久删除单条通知（物理删除）
  delete: (id: string) => apiService.delete(`/api/admin/notifications/${id}`),
  // DELETE /api/admin/notifications/clear-all 管理员永久清空全部通知
  clearAll: () => apiService.delete('/api/admin/notifications/clear-all')
}

export const serviceApi = {
  getServices: (params?: any) => apiService.get('/api/data/services', params),
  getService: (id: string) => apiService.get(`/api/data/services/${id}`),
  createService: (data: any) => apiService.post('/api/data/services', data),
  updateService: (id: string, data: any) => apiService.put(`/api/data/services/${id}`, data),
  deleteService: (id: string) => apiService.delete(`/api/data/services/${id}`),
  // V1.3.16：服务统计卡片（总服务数/启用/APP/小程序）与真实目录
  getServiceStats: () => apiService.get('/api/admin/stats/services'),
  getServiceCatalog: () => apiService.get('/api/admin/services/catalog')
}

export const advertisementApi = {
  getAdvertisements: (params?: any) => apiService.get('/api/data/advertisements', params),
  getAdvertisement: (id: string) => apiService.get(`/api/data/advertisements/${id}`),
  createAdvertisement: (data: any) => apiService.post('/api/data/advertisements', data),
  updateAdvertisement: (id: string, data: any) => apiService.put(`/api/data/advertisements/${id}`, data),
  deleteAdvertisement: (id: string) => apiService.delete(`/api/data/advertisements/${id}`)
}

// ========== 监护宝管理 API ==========
export const guardianDeviceApi = {
  getDevices: (params?: any) => apiService.get('/api/guardian/devices', params),
  getDevice: (id: string) => apiService.get(`/api/guardian/devices/${id}`),
  createDevice: (data: any) => apiService.post('/api/guardian/devices', data),
  updateDevice: (id: string, data: any) => apiService.put(`/api/guardian/devices/${id}`, data),
  deleteDevice: (id: string) => apiService.delete(`/api/guardian/devices/${id}`),
  getDevicesByType: (type: string) => apiService.get(`/api/guardian/devices/type/${type}`),
  getDevicesByStatus: (status: string) => apiService.get(`/api/guardian/devices/status/${status}`),
  getOnlineDevices: () => apiService.get('/api/guardian/devices/online'),
  updateDeviceStatus: (id: string, status: string) => apiService.patch(`/api/guardian/devices/${id}/status`, { status }),
  getDeviceStats: () => apiService.get('/api/guardian/devices/stats')
}

// AI模块统一真实统计（V1.3.40：30+子页面共用数据源）
export const aiStatsApi = {
  getStats: () => apiService.get('/api/ai-stats/stats')
}

// 系统日志统计（V1.3.40：SystemLogsView/ErrorLogsView/UserActivityView 真实数据源；V1.5.15：手动删除/批量删除）
export const logsApi = {
  getStats: (days?: number) => apiService.get('/api/admin/stats/logs', days ? { days } : undefined),
  getList: (params?: any) => apiService.get('/api/admin/logs', params),
  getDetail: (id: string) => apiService.get(`/api/admin/logs/${id}`),
  remove: (id: string) => apiService.delete(`/api/admin/logs/${id}`),
  batchDelete: (data: { ids?: string[]; all?: boolean; level?: string }) => apiService.post('/api/admin/logs/batch-delete', data)
}

// 用户标签（V1.3.40：UserTagsView 真实数据源）
export const userTagsApi = {
  list: () => apiService.get('/api/admin/stats/user-tags'),
  create: (data: any) => apiService.post('/api/admin/stats/user-tags', data),
  update: (id: string, data: any) => apiService.put(`/api/admin/stats/user-tags/${id}`, data),
  delete: (id: string) => apiService.delete(`/api/admin/stats/user-tags/${id}`)
}

export const guardianAlertApi = {
  getAlerts: (params?: any) => apiService.get('/api/guardian/alerts', params),
  getAlert: (id: string) => apiService.get(`/api/guardian/alerts/${id}`),
  createAlert: (data: any) => apiService.post('/api/guardian/alerts', data),
  updateAlert: (id: string, data: any) => apiService.put(`/api/guardian/alerts/${id}`, data),
  deleteAlert: (id: string) => apiService.delete(`/api/guardian/alerts/${id}`),
  getAlertsByDevice: (deviceId: string) => apiService.get(`/api/guardian/alerts/device/${deviceId}`),
  getAlertsByType: (type: string) => apiService.get(`/api/guardian/alerts/type/${type}`),
  getAlertsByLevel: (level: string) => apiService.get(`/api/guardian/alerts/level/${level}`),
  getAlertsByStatus: (status: string) => apiService.get(`/api/guardian/alerts/status/${status}`),
  confirmAlert: (id: string) => apiService.patch(`/api/guardian/alerts/${id}/confirm`),
  resolveAlert: (id: string, note: string) => apiService.patch(`/api/guardian/alerts/${id}/resolve`, { note }),
  getRecentAlerts: (limit: number = 10) => apiService.get(`/api/guardian/alerts/recent?limit=${limit}`),
  getAlertStats: (params?: any) => apiService.get('/api/guardian/alerts/stats', params),
  getAlertTrends: (days: number = 30) => apiService.get(`/api/guardian/alerts/trends?days=${days}`),
  getSettings: () => apiService.get('/api/guardian/alerts/settings'),
  saveSettings: (data: any) => apiService.post('/api/guardian/alerts/settings', data),
  processAlert: (id: string) => apiService.patch(`/api/guardian/alerts/${id}/process`)
}

export const guardianBindingApi = {
  getBindings: (params?: any) => apiService.get('/api/guardian/bindings', params),
  getBinding: (id: string) => apiService.get(`/api/guardian/bindings/${id}`),
  createBinding: (data: any) => apiService.post('/api/guardian/bindings', data),
  updateBinding: (id: string, data: any) => apiService.put(`/api/guardian/bindings/${id}`, data),
  deleteBinding: (id: string) => apiService.delete(`/api/guardian/bindings/${id}`),
  getBindingsByUser: (userId: string) => apiService.get(`/api/guardian/bindings/user/${userId}`),
  getBindingsByDevice: (deviceId: string) => apiService.get(`/api/guardian/bindings/device/${deviceId}`),
  unbindDevice: (id: string) => apiService.patch(`/api/guardian/bindings/${id}/unbind`),
  getBindingStats: () => apiService.get('/api/guardian/bindings/stats')
}

export const guardianApi = {
  getOverview: () => apiService.get('/api/guardian/overview'),
  getDashboardStats: () => apiService.get('/api/guardian/dashboard/stats'),
  getStatistics: () => apiService.get('/api/guardian/statistics')
}

// ========== 家族谱管理 API ==========
export const familyTreeApi = {
  getTrees: (params?: any) => apiService.get('/api/family-tree/trees', params),
  getTree: (id: string) => apiService.get(`/api/family-tree/trees/${id}`),
  createTree: (data: any) => apiService.post('/api/family-tree/trees', data),
  updateTree: (id: string, data: any) => apiService.put(`/api/family-tree/trees/${id}`, data),
  deleteTree: (id: string) => apiService.delete(`/api/family-tree/trees/${id}`),
  getTreesByCreator: (creatorId: string) => apiService.get(`/api/family-tree/trees/creator/${creatorId}`),
  getTreesByStatus: (status: string) => apiService.get(`/api/family-tree/trees/status/${status}`),
  getTreeStats: () => apiService.get('/api/family-tree/trees/stats')
}

export const familyMemberApi = {
  getMembers: (params?: any) => apiService.get('/api/family-tree/members', params),
  getMember: (id: string) => apiService.get(`/api/family-tree/members/${id}`),
  createMember: (data: any) => apiService.post('/api/family-tree/members', data),
  updateMember: (id: string, data: any) => apiService.put(`/api/family-tree/members/${id}`, data),
  deleteMember: (id: string) => apiService.delete(`/api/family-tree/members/${id}`),
  getMembersByTree: (treeId: string) => apiService.get(`/api/family-tree/members/tree/${treeId}`),
  getMembersByGender: (gender: string) => apiService.get(`/api/family-tree/members/gender/${gender}`),
  getMembersByGeneration: (generation: number) => apiService.get(`/api/family-tree/members/generation/${generation}`),
  getMemberStats: (treeId?: string) => apiService.get(treeId ? `/api/family-tree/members/stats?treeId=${treeId}` : '/api/family-tree/members/stats')
}

export const familyRelationApi = {
  getRelations: (params?: any) => apiService.get('/api/family-tree/relations', params),
  getRelation: (id: string) => apiService.get(`/api/family-tree/relations/${id}`),
  createRelation: (data: any) => apiService.post('/api/family-tree/relations', data),
  updateRelation: (id: string, data: any) => apiService.put(`/api/family-tree/relations/${id}`, data),
  deleteRelation: (id: string) => apiService.delete(`/api/family-tree/relations/${id}`),
  getRelationsByTree: (treeId: string) => apiService.get(`/api/family-tree/relations/tree/${treeId}`),
  getRelationsByType: (type: string) => apiService.get(`/api/family-tree/relations/type/${type}`),
  getMemberRelations: (memberId: string) => apiService.get(`/api/family-tree/relations/member/${memberId}`),
  getRelationStats: (treeId?: string) => apiService.get(treeId ? `/api/family-tree/relations/stats?treeId=${treeId}` : '/api/family-tree/relations/stats')
}

export const familyTreeDashboardApi = {
  getOverview: () => apiService.get('/api/family-tree/overview'),
  getDashboardStats: () => apiService.get('/api/family-tree/dashboard/stats'),
  getStatistics: () => apiService.get('/api/family-tree/statistics')
}

export const familyTreeSettingsApi = {
  getSettings: () => apiService.get('/api/family-tree/settings'),
  saveSettings: (data: any) => apiService.post('/api/family-tree/settings', data)
}

// V1.0.52：家族谱家庭共享组管理（关联多账号共同编辑）
export const familyShareGroupApi = {
  getGroups: (keyword?: string) => apiService.get('/api/family-tree/admin/share-groups', keyword ? { keyword } : undefined),
  removeMember: (ownerUserId: string, memberUserId: string) => apiService.delete(`/api/family-tree/admin/share-groups/${encodeURIComponent(ownerUserId)}/members/${encodeURIComponent(memberUserId)}`),
  dissolveGroup: (ownerUserId: string) => apiService.delete(`/api/family-tree/admin/share-groups/${encodeURIComponent(ownerUserId)}`)
}

// 公司账户详情-支付方式真实统计（orders真实支付记录+paymentConfig真实商户/对公账户配置）
export const bankAccountApi = {
  getPaymentStats: () => apiService.get('/api/admin/bank-account/payment-stats')
}

// V1.3.26：会议模块 API（原概览页调错 xiebanServiceApi.getServices）
export const meetingsApi = {
  getOverview: () => apiService.get('/api/meetings/overview'),
  getList: () => apiService.get('/api/meetings/list'),
  getStats: () => apiService.get('/api/meetings/stats'),
  getMeeting: (id: string) => apiService.get(`/api/meetings/${id}`),
  createMeeting: (data: any) => apiService.post('/api/meetings/create', data),
  updateMeeting: (id: string, data: any) => apiService.put(`/api/meetings/${id}`, data),
  deleteMeeting: (id: string) => apiService.delete(`/api/meetings/${id}`),
  getMessages: (meetingId: string) => apiService.get(`/api/meetings/messages/list?meetingId=${meetingId}`),
  sendMessage: (data: any) => apiService.post('/api/meetings/messages/send', data)
}

export const xiebanServiceApi = {
  getServices: (params?: any) => apiService.get('/api/services/services', params),
  getService: (id: string) => apiService.get(`/api/services/services/${id}`),
  createService: (data: any) => apiService.post('/api/services/services', data),
  updateService: (id: string, data: any) => apiService.put(`/api/services/services/${id}`, data),
  deleteService: (id: string) => apiService.delete(`/api/services/services/${id}`),
  // V1.3.20：版本路由真实挂载于 /api/xieban（xieban-service.js），原 /api/services/versions 必404致版本管理页空表
  getVersions: (params?: any) => apiService.get('/api/xieban/versions', params),
  getVersion: (id: string) => apiService.get(`/api/xieban/versions/${id}`),
  createVersion: (data: any) => apiService.post('/api/xieban/versions', data),
  updateVersion: (id: string, data: any) => apiService.put(`/api/xieban/versions/${id}`, data),
  deleteVersion: (id: string) => apiService.delete(`/api/xieban/versions/${id}`),
  getContentList: (params?: any) => apiService.get('/api/services/content', params),
  getContent: (id: string) => apiService.get(`/api/services/content/${id}`),
  createContent: (data: any) => apiService.post('/api/services/content', data),
  updateContent: (data: any) => apiService.put(`/api/services/content/${data.id}`, data),
  updateContentStatus: (id: string, status: string) => apiService.patch(`/api/services/content/${id}/status`, { status }),
  deleteContent: (id: string) => apiService.delete(`/api/services/content/${id}`),
  importContent: (file: File) => apiService.upload('/api/services/content/import', file),
  exportContent: (params?: any) => apiService.download('/api/services/content/export', params)
}

export const xiebanTalentApi = {
  getTalents: (params?: any) => apiService.get('/api/xieban-talent/list', params),
  getTalent: (id: string) => apiService.get(`/api/xieban-talent/${id}`),
  getTalentById: (id: string) => apiService.get(`/api/xieban-talent/${id}`),
  createTalent: (data: any) => apiService.post('/api/xieban-talent', data),
  updateTalent: (id: string, data: any) => apiService.put(`/api/xieban-talent/${id}`, data),
  deleteTalent: (id: string) => apiService.delete(`/api/xieban-talent/${id}`),
  // 修复：新增达人统计+趋势图API（真实Service调用，原硬编码假数据移除）
  getStats: (params?: any) => apiService.get('/api/xieban-talent/stats', params),
  getTrendChart: (params?: any) => apiService.get('/api/xieban-talent/trend-chart', params),
  getLevelDistribution: (params?: any) => apiService.get('/api/xieban-talent/level-distribution', params),
  getServiceTypeDistribution: (params?: any) => apiService.get('/api/xieban-talent/service-distribution', params),
  getOrders: (params?: any) => apiService.get('/api/xieban-talent/orders', params),
  getOrder: (id: string) => apiService.get(`/api/xieban-talent/orders/${id}`),
  createOrder: (data: any) => apiService.post('/api/xieban-talent/orders', data),
  updateOrder: (id: string, data: any) => apiService.put(`/api/xieban-talent/orders/${id}`, data),
  deleteOrder: (id: string) => apiService.delete(`/api/xieban-talent/orders/${id}`),
  getServices: (params?: any) => apiService.get('/api/xieban-talent/services', params)
}

export const aiApi = {
  getAnalytics: (params?: any) => apiService.get('/api/ai/analytics', params),
  getPrediction: (params?: any) => apiService.get('/api/ai/prediction', params),
  getRecommendation: (params?: any) => apiService.get('/api/ai/recommendation', params),
  getInsights: (params?: any) => apiService.get('/api/ai/insights', params),
  // 修复：新增AI预测分析相关API（真实Service调用，原硬编码假数据移除）
  getForecastAnalysis: (params?: any) => apiService.get('/api/ai/analytics/prediction', params),
  getForecastCharts: (params?: any) => apiService.get('/api/ai/analytics/forecast-charts', params),
  getForecastInsights: (params?: any) => apiService.get('/api/ai/analytics/forecast-insights', params)
}

export const platformApi = {
  getDashboard: () => apiService.get('/platform/dashboard')
}

export const courseApi = {
  getCourses: (params?: any) => apiService.get('/api/services/courses', params),
  getCourse: (id: string) => apiService.get(`/api/services/courses/${id}`),
  createCourse: (data: any) => apiService.post('/api/services/courses', data),
  updateCourse: (id: string, data: any) => apiService.put(`/api/services/courses/${id}`, data),
  deleteCourse: (id: string) => apiService.delete(`/api/services/courses/${id}`),
  getCourseCategories: (params?: any) => apiService.get('/api/services/courses/categories', params),
  getCourseCategory: (id: string) => apiService.get(`/api/services/courses/categories/${id}`),
  addCourseCategory: (data: any) => apiService.post('/api/services/courses/categories', data),
  updateCourseCategory: (data: any) => apiService.put(`/api/services/courses/categories/${data.id}`, data),
  deleteCourseCategory: (id: string) => apiService.delete(`/api/services/courses/categories/${id}`),
  getCourseChapters: (courseId: string) => apiService.get(`/api/services/courses/${courseId}/chapters`),
  getCourseLessons: (courseId: string, chapterId: string) => apiService.get(`/api/services/courses/${courseId}/chapters/${chapterId}/lessons`),
  batchImportCourses: (data: any) => apiService.post('/api/services/courses/batch-import', data),
  post: (url: string, data: any) => apiService.post(url, data),
  put: (url: string, data: any) => apiService.put(url, data),
  delete: (url: string) => apiService.delete(url),
  get: (url: string, params?: any) => apiService.get(url, params)
}

export const questionBankApi = {
  getQuestions: (params?: any) => apiService.get('/api/services/question-bank', params),
  getQuestion: (id: string) => apiService.get(`/api/services/question-bank/${id}`),
  createQuestion: (data: any) => apiService.post('/api/services/question-bank', data),
  updateQuestion: (id: string, data: any) => apiService.put(`/api/services/question-bank/${id}`, data),
  deleteQuestion: (id: string) => apiService.delete(`/api/services/question-bank/${id}`),
  getQuestionStats: () => apiService.get('/api/services/question-bank/stats'),
  getQuestionCategories: (params?: any) => apiService.get('/api/services/question-bank/categories', params),
  getQuestionCategory: (id: string) => apiService.get(`/api/services/question-bank/categories/${id}`),
  addQuestionCategory: (data: any) => apiService.post('/api/services/question-bank/categories', data),
  updateQuestionCategory: (data: any) => apiService.put(`/api/services/question-bank/categories/${data.id}`, data),
  deleteQuestionCategory: (id: string) => apiService.delete(`/api/services/question-bank/categories/${id}`),
  batchImportQuestions: (data: any) => apiService.post('/api/services/question-bank/batch-import', data),
  getQuestionsByCourse: (courseId: string, params?: any) => apiService.get(`/api/services/question-bank/course/${courseId}`, params),
  getQuestionsByCategory: (category: string, params?: any) => apiService.get(`/api/services/question-bank/category/${category}`, params),
  getQuestionsByDifficulty: (difficulty: string, params?: any) => apiService.get(`/api/services/question-bank/difficulty/${difficulty}`, params),
  post: (url: string, data: any) => apiService.post(url, data),
  put: (url: string, data: any) => apiService.put(url, data),
  delete: (url: string) => apiService.delete(url),
  get: (url: string, params?: any) => apiService.get(url, params)
}

export const courseQuestionApi = {
  getRelations: (params?: any) => apiService.get('/api/services/course-question-relations', params),
  createRelation: (data: any) => apiService.post('/api/services/course-question-relations', data),
  updateRelation: (id: string, data: any) => apiService.put(`/api/services/course-question-relations/${id}`, data),
  deleteRelation: (id: string) => apiService.delete(`/api/services/course-question-relations/${id}`),
  syncRelations: () => apiService.post('/api/services/course-question-relations/sync'),
  generateRelations: (data: any) => apiService.post('/api/services/course-question-relations/generate', data)
}

// ========== 数据同步管理 API ==========
export const dataSourceApi = {
  getDataSources: (params?: any) => apiService.get('/api/data-sync/data-sources', params),
  getDataSource: (id: string) => apiService.get(`/api/data-sync/data-sources/${id}`),
  createDataSource: (data: any) => apiService.post('/api/data-sync/data-sources', data),
  updateDataSource: (id: string, data: any) => apiService.put(`/api/data-sync/data-sources/${id}`, data),
  deleteDataSource: (id: string) => apiService.delete(`/api/data-sync/data-sources/${id}`),
  testConnection: (id: string) => apiService.post(`/api/data-sync/data-sources/${id}/test-connection`),
  testAllConnections: () => apiService.post('/api/data-sync/data-sources/test-all-connections'),
  getDataSourceStats: () => apiService.get('/api/data-sync/data-sources/stats')
}

export const syncConfigApi = {
  getSyncConfigs: (params?: any) => apiService.get('/api/data-sync/sync-configs', params),
  getSyncConfig: (id: string) => apiService.get(`/api/data-sync/sync-configs/${id}`),
  createSyncConfig: (data: any) => apiService.post('/api/data-sync/sync-configs', data),
  updateSyncConfig: (id: string, data: any) => apiService.put(`/api/data-sync/sync-configs/${id}`, data),
  deleteSyncConfig: (id: string) => apiService.delete(`/api/data-sync/sync-configs/${id}`),
  enableSyncConfig: (id: string) => apiService.patch(`/api/data-sync/sync-configs/${id}/enable`),
  disableSyncConfig: (id: string) => apiService.patch(`/api/data-sync/sync-configs/${id}/disable`)
}

export const syncTaskApi = {
  getSyncTasks: (params?: any) => apiService.get('/api/data-sync/sync-tasks', params),
  getSyncTask: (id: string) => apiService.get(`/api/data-sync/sync-tasks/${id}`),
  createSyncTask: (data: any) => apiService.post('/api/data-sync/sync-tasks', data),
  updateSyncTask: (id: string, data: any) => apiService.put(`/api/data-sync/sync-tasks/${id}`, data),
  deleteSyncTask: (id: string) => apiService.delete(`/api/data-sync/sync-tasks/${id}`),
  startSyncTask: (id: string) => apiService.post(`/api/data-sync/sync-tasks/${id}/start`),
  stopSyncTask: (id: string) => apiService.post(`/api/data-sync/sync-tasks/${id}/stop`),
  startAllTasks: () => apiService.post('/api/data-sync/sync-tasks/start-all'),
  stopAllTasks: () => apiService.post('/api/data-sync/sync-tasks/stop-all'),
  getSyncTaskStats: () => apiService.get('/api/data-sync/sync-tasks/stats')
}

export const syncRecordApi = {
  getSyncRecords: (params?: any) => apiService.get('/api/data-sync/sync-records', params),
  getSyncRecord: (id: string) => apiService.get(`/api/data-sync/sync-records/${id}`),
  deleteSyncRecord: (id: string) => apiService.delete(`/api/data-sync/sync-records/${id}`),
  retrySync: (id: string) => apiService.post(`/api/data-sync/sync-records/${id}/retry`),
  exportSyncRecords: (params?: any) => apiService.download('/api/data-sync/sync-records/export', params),
  getSyncRecordStats: (params?: any) => apiService.get('/api/data-sync/sync-records/stats', params)
}

// ========== 客服聊天 API ==========
export const chatApi = {
  getChatUsers: (params?: any) => apiService.get('/chat/users', params),
  getChatUser: (id: string) => apiService.get(`/chat/users/${id}`),
  getChatMessages: (userId: string, params?: any) => apiService.get(`/chat/users/${userId}/messages`, params),
  sendChatMessage: (userId: string, data: any) => apiService.post(`/chat/users/${userId}/messages`, data),
  markMessagesRead: (userId: string) => apiService.patch(`/chat/users/${userId}/mark-read`),
  getUnreadCount: () => apiService.get('/chat/unread-count'),
  getUserProfile: (userId: string) => apiService.get(`/chat/users/${userId}/profile`),
  // 修复：新增消息审核相关API（真实Service调用，原硬编码假数据移除）
  listAuditMessages: (params?: any) => apiService.get('/api/chat/audit/list', params),
  getAuditStats: (params?: any) => apiService.get('/api/chat/audit/stats', params),
  approveAuditMessage: (id: string, data?: any) => apiService.post(`/api/chat/audit/${id}/approve`, data),
  rejectAuditMessage: (id: string, data?: any) => apiService.post(`/api/chat/audit/${id}/reject`, data),
  batchApproveAuditMessages: (data: any) => apiService.post('/api/chat/audit/batch-approve', data),
  batchRejectAuditMessages: (data: any) => apiService.post('/api/chat/audit/batch-reject', data)
}

// ========== 谐伴团队管理 API（V1.5.20：真实CRUD） ==========
export const teamApi = {
  getTeams: (params?: any) => apiService.get('/api/team/teams', params),
  getTeam: (id: string) => apiService.get(`/api/team/teams/${id}`),
  createTeam: (data: any) => apiService.post('/api/team/teams', data),
  updateTeam: (id: string, data: any) => apiService.put(`/api/team/teams/${id}`, data),
  deleteTeam: (id: string) => apiService.delete(`/api/team/teams/${id}`),
  toggleStatus: (id: string, status: string) => apiService.patch(`/api/team/teams/${id}/status`, { status })
}

// ========== 扫一扫管理 API（V1.5.24：设置持久化+风险预警删除） ==========
export const scanAdminApi = {
  getSettings: () => apiService.get('/api/admin/scan-settings'),
  saveSettings: (data: any) => apiService.post('/api/admin/scan-settings', data),
  deleteRisk: (id: string) => apiService.delete(`/api/admin/scan/risks/${id}`),
  batchDeleteRisks: (data: { ids?: string[]; all?: boolean }) => apiService.post('/api/admin/scan/risks/batch-delete', data)
}
